/* Copyright (c) 2020-present CloudIO Inc */

import {
  Attribute,
  DataRequest,
  DataResponse,
  DBRow,
  Filters,
  hasWSError,
  IoDatasourceAttributes,
  NewRow,
  Query,
  RecordStatus,
  RootRequest,
  Row,
  SortType,
  WSResponse,
} from '@cloudio-saas/datasource-types';
import { areEqualShallow, emptyFunction, Path } from '@cloudio-saas/statex';
import { Item, ItemID, Trigger } from 'componentregistry';
import type { Platform } from 'componentregistry/platform/Platform';
import { DataStoreTriggers } from 'components/DataStore/useDataStoreTriggers';
import { applyFilters } from 'components/SmartSearch/ArrayFilters';
import { makeValid } from 'components/SmartSearch/SearchUtils';
import stringify from 'json-stable-stringify';
import AbortError from 'platform/AbortError';
import IOError from 'platform/IOError';
import { STORE } from 'platform/symbols';
import ReactDOM from 'react-dom';
import { getErrorMessage, isEmpty, isNotEmpty, isNull } from 'utils/isEmpty';
import Logger from 'utils/Logger';
import { PubSub as PubSubClass } from 'utils/PubSub';
import { handleCatchError } from '../triggers/useTrigger';
import {
  applyActionFilters,
  getActionSetValues,
  invokeTrigger,
  isTriggerOrActionEnabled,
} from './StoreTriggersUtils';
import { isFromDB, Records } from './storeTypes';
import DSAttributesCache, {
  attributesPath,
  pkAttributesPath,
} from './DSAttributesCache';
import { replaceKeyAndValue, updateValue } from './utils/utils';
import { SINGLETON } from 'componentregistry/registry/constants';
import { getUrl } from 'config/config';
import { getURLQueryStringToPost } from 'utils/url';
import { isAdministrator, isDeveloper, userAtom } from 'root/useUser';
import { startPageProgress } from '../components/Page/PageProgressed';
import {
  LocalDataStoreItem,
  preCompile,
} from 'componentregistry/components/LocalDataStore/LocalDataStore';
import {
  parseCSV,
  parseTSData,
  parseJSON,
} from 'components/LocalDataStore/DataUtils';
import { maskPageSignal } from 'components/Page/MaskPage';
import { getFilterFromActions } from 'actions/ActionQuery/ActionQuery';

export const NULL_SYMBOL = Symbol('NULL');
export const EMPTY_OBJECT = Object.freeze({});
export const EMPTY_ARRAY = Object.freeze([]);
export const MISSING_STORE_PATH = ['_missing_store'];

export const resolvedPromise = new Promise<boolean>((resolve) => resolve(true));

export const MAX_ROWS = 200;
let storeIdCounter = 0;
let counter = 0;
function newRecordID() {
  return `r${++counter}`;
}

let cloudRequestCounter = 0;
function nextCloudRequestId() {
  return `cr${++cloudRequestCounter}`;
}

export const initialState = {
  transient: { sidebar: { propertySection: { Common: false, ui: false } } },
};

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export interface QueryProps<T> extends Query<T> {
  force?: boolean;
  abortMessage?: string;
  parallelQuery?: boolean;
  ignoreSpecialCharacters?: boolean;
  specialCharacters?: string;
}

export type DataStoreType =
  | 'CloudDataStore'
  | 'DataStore'
  | 'ReadOnlyDataStore'
  | 'TransientDataStore'
  | 'LocalDataStore';

export type QueryRequest<T> = {
  props?: QueryProps<T>;
  noClear?: boolean;
  skipApplyingFilters?: boolean;
  batchCallback?: () => void;
  cancelBatch?: () => void;
  queryingItemId?: string;
};

type TreeNodeState = {
  expanded: boolean;
  children: string[];
  loaded: boolean;
  parent: string | null;
};

type TreeState = Record<string, TreeNodeState>;

type StoreTreeMetaData<T> = {
  state: TreeState;
  nodeIdAttribute: keyof T;
  parentNodeIdAttribute: keyof T;
  initialized: boolean;
  initialExpanded: boolean;
};

interface StoreEventMap<T> {
  OnRecordActive: { id: string | null | undefined };
  OnCreateNewRecord: { id: string; record: NewRow<T> };
  OnBeforePost: { request: DataRequest<T> };
  OnStoreClear: undefined;
  OnAfterQuery: { rows: DBRow<T>[]; records: Records<T> };
  OnBeforeQuery: { queryProps: QueryProps<T> };
}

const EXCLUDE_ATTRIBUTES = [
  '_id',
  '_rs',
  '_ca',
  '_changedAttributes',
  '_orig',
  '_newKeys',
  '_ov',
  '_cid',
  'creationDate',
  'createdBy',
  'lastUpdateDate',
  'lastUpdatedBy',
];

class Store<T> {
  $$typeof = STORE;

  id: string;

  ds: string;

  displayName: string;

  alias: string;

  maxRows = MAX_ROWS;

  pubsub = new PubSubClass<StoreEventMap<T>>();

  platform: Platform;

  programmatic: boolean;

  filterLocally: boolean;

  localStore = false;

  cloudStore = false;

  singleton: boolean;

  recordsPath: Path;

  originalRecordsPath: Path;

  originalRecordsOrderPath: Path;

  recordsFreqPath: Path;

  recordsOrderPath: Path;

  recordsOrderIndexPath: Path;

  recordPath: Path;

  errorPath: Path;

  errorMapPath: Path;

  recordStatusPath: Path;

  originalRecordIndexPath: Path;

  deletedRecordsIndexMapPath: Path;

  currentRecordIdPath: Path;

  isBusyPath: Path;

  insertablePath: Path;

  updatablePath: Path;

  deletablePath: Path;

  requestIDPath: Path;

  sortPath: Path;

  initialQueryFiredPath: Path;

  autoQueryFiredPath: Path;

  attributesInitializedPath: Path;

  attributePath: Path;

  origAttributePath: Path;

  origPath: Path;

  selectedRecordIdsPath: Path;

  smartSearchFilterPath: Path;

  gridFilterPath: Path;

  beforeQueryTrigger: Trigger | undefined;

  afterQueryTrigger: Trigger | undefined;

  beforeCreateTrigger: Trigger | undefined;

  beforeSaveTrigger: Trigger | undefined;

  afterSaveTrigger: Trigger | undefined;

  beforeInsertStoreRecordTrigger: Trigger | undefined;

  afterInsertStoreRecordTrigger: Trigger | undefined;

  beforeDeleteRecordTrigger: Trigger | undefined;

  afterDeleteRecordTrigger: Trigger | undefined;

  onRecordActiveTrigger: Trigger | undefined;

  itemId: ItemID | undefined;

  itemType: DataStoreType;

  _pageId: string;

  appUid: string;

  dsAppUid: string | undefined;

  feedback = '';

  queryable = true;

  autoQuery = true;

  hasMore = false;

  pagination: Record<string, string> | undefined;

  cacheAll = false;

  reverse = false;

  transient = false;

  _cache = new Map<string, DBRow<T>[]>();

  _cacheReqBody = new Map<string, RootRequest<T>>();

  data: Record<string, unknown> = {};

  previousQuery = '';

  previousQueryBody?: RootRequest<T>;

  previousPromise?: Promise<void | Records<T> | undefined>;

  previousResult?: Records<T>;

  previousError?: Error;

  _idCounter = 0;

  treeStatePath: string[];

  ignorePKDuplicate: boolean = false;

  doNotMonitorQueryRequest: boolean = false;

  hasPagination: boolean = false;

  totalRowCountPath: Path;

  controlledBy: string | undefined;

  hideDirtyIndicator: boolean = false;

  storeQueried: boolean = false;

  maskPageOnPost: boolean = false;

  requestId: string | undefined;

  maskPageOnQuery: boolean = false;
  get pageId() {
    return this._pageId;
  }

  getPath = (
    recordsType?: string,
    indexKey?: string,
    prop?: string,
    subprop?: string,
  ): string[] => {
    let path = [
      'root',
      'dataStore',
      this.appUid,
      this.singleton ? SINGLETON : this._pageId,
      this.ds,
      this.alias,
    ];
    if (recordsType && indexKey && prop && subprop) {
      path = [...path, recordsType, indexKey, prop, subprop];
    } else if (recordsType && indexKey && prop) {
      path = [...path, recordsType, indexKey, prop];
    } else if (recordsType && indexKey) {
      path = [...path, recordsType, indexKey];
    } else if (recordsType) {
      path = [...path, recordsType];
    }
    return path;
  };

  priority = Number.MAX_VALUE;

  constructor(props: {
    ds: string;
    alias: string;
    displayName: string;
    platform: Platform;
    programmatic: boolean;
    filterLocally: boolean;
    appUid: string;
    dsAppUid?: string;
    pageId: string;
    triggers?: DataStoreTriggers;
    itemId?: ItemID;
    itemType: DataStoreType;
    maxRows?: number;
    singleton: boolean;
    controlledBy?: string;
  }) {
    const {
      ds,
      alias,
      displayName,
      platform,
      programmatic,
      filterLocally,
      appUid,
      pageId,
      dsAppUid,
      triggers,
      itemId,
      itemType,
      maxRows,
      singleton,
      controlledBy,
    } = props;
    this.id = `s${++storeIdCounter}`;
    this.itemId = itemId;
    this.itemType = itemType;
    this._pageId = pageId;
    this.appUid = appUid;
    this.dsAppUid = dsAppUid;
    this.controlledBy = controlledBy;
    if (triggers) {
      const {
        afterQueryTrigger,
        afterSaveTrigger,
        beforeCreateTrigger,
        beforeQueryTrigger,
        beforeSaveTrigger,
        beforeInsertStoreRecordTrigger,
        afterInsertStoreRecordTrigger,
        beforeDeleteRecordTrigger,
        afterDeleteRecordTrigger,
        onRecordActiveTrigger,
      } = triggers;
      this.afterQueryTrigger = afterQueryTrigger;
      this.beforeQueryTrigger = beforeQueryTrigger;
      this.beforeCreateTrigger = beforeCreateTrigger;
      this.beforeSaveTrigger = beforeSaveTrigger;
      this.afterSaveTrigger = afterSaveTrigger;
      this.beforeInsertStoreRecordTrigger = beforeInsertStoreRecordTrigger;
      this.afterInsertStoreRecordTrigger = afterInsertStoreRecordTrigger;
      this.beforeDeleteRecordTrigger = beforeDeleteRecordTrigger;
      this.afterDeleteRecordTrigger = afterDeleteRecordTrigger;
      this.onRecordActiveTrigger = onRecordActiveTrigger;
    }
    this.ds = ds;
    this.alias = alias;
    this.displayName = displayName || `store ${alias}`;
    this.maxRows = maxRows || MAX_ROWS;
    this.platform = platform;
    this.programmatic = programmatic;
    this.filterLocally = filterLocally;
    this.singleton = singleton;
    this.recordsPath = this.getPath('records');
    this.totalRowCountPath = this.getPath('totalRowCount');
    this.originalRecordsPath = this.getPath('originalRecords');
    this.originalRecordsOrderPath = this.getPath('originalRecordsOrder');
    this.recordsFreqPath = this.getPath('recordsFreq');
    this.recordsOrderPath = this.getPath('recordsOrder');
    this.recordsOrderIndexPath = this.getPath('recordsOrder', ':id');
    this.recordPath = this.getPath('records', ':id');
    this.recordStatusPath = this.getPath('records', ':id', '_rs');
    this.originalRecordIndexPath = this.getPath('originalRecords', ':id');
    this.deletedRecordsIndexMapPath = this.getPath('deletedRecordsIndex');
    this.currentRecordIdPath = this.getPath('currentRecordId');
    this.isBusyPath = this.getPath('busy');
    this.insertablePath = this.getPath('insertable');
    this.updatablePath = this.getPath('updatable');
    this.deletablePath = this.getPath('deletable');
    this.requestIDPath = this.getPath('rid');
    this.sortPath = this.getPath('sort');
    this.initialQueryFiredPath = this.getPath('initialQueryFired');
    this.autoQueryFiredPath = this.getPath('autoQueryFired');
    this.attributePath = this.getPath('records', ':id', ':attribute');
    this.origAttributePath = this.getPath(
      'records',
      ':id',
      '_orig',
      ':attribute',
    );
    this.origPath = this.getPath('records', ':id', '_orig');
    this.selectedRecordIdsPath = this.getPath('selectedRecordIds');
    this.errorPath = this.getPath('errorMap', ':id', ':attribute');
    this.errorMapPath = this.getPath('errorMap');
    this.smartSearchFilterPath = this.getPath('smartSearchFilter');
    this.gridFilterPath = this.getPath('gridFilter');
    this.attributesInitializedPath = this.getPath('attributesInitialized');
    this.treeStatePath = this.getPath('treeState');
  }

  records = (): Records<T> => {
    return this.platform.getter(this.recordsPath) ?? EMPTY_OBJECT;
  };

  getRecords = (): Row<T>[] => {
    return Object.values(this.records());
  };

  originalRecords = (): Records<T> => {
    return this.platform.getter(this.originalRecordsPath) ?? EMPTY_OBJECT;
  };

  originalRecordsOrder = (): string[] => {
    return this.platform.getter(this.originalRecordsOrderPath) ?? EMPTY_ARRAY;
  };

  setOriginalRecordsOrder = (recordsOrder: string[]) => {
    this.platform.setter(this.originalRecordsOrderPath, recordsOrder);
  };

  setRecords = (records: Records<T>) => {
    this.platform.setter(this.recordsPath, records);
  };

  setOriginalRecords = (records: Records<T>) => {
    this.platform.setter(this.originalRecordsPath, records);
  };

  recordsOrder = (): string[] => {
    return this.platform.getter(this.recordsOrderPath) ?? EMPTY_ARRAY;
  };

  getRecordIdAtRow = (rowIndex: number): string => {
    const id = this.recordsOrder()[rowIndex];
    if (!id) {
      console.warn(`Missing record at index ${rowIndex}!`);
    }
    return id;
  };

  getRecordAtRowIndex = (rowIndex: number): Row<T> => {
    const id = this.recordsOrder()[rowIndex];
    if (!id) {
      console.warn(`Missing record at index ${rowIndex}!`);
    }
    return this.getRecord(id ?? 'x');
  };

  size = () => {
    return this.recordsOrder().length;
  };

  totalRowCount = (): number => {
    return this.platform.getter(this.totalRowCountPath) ?? 0;
  };

  setTotalRowCount = (value: number | undefined | null) => {
    this.platform.setter(this.totalRowCountPath, value || null);
  };

  setRecordsOrder = (values: (string | undefined)[]) => {
    this.platform.setter(this.recordsOrderPath, values);
  };

  setRecord = (id: string, record: Row<T>) => {
    this.platform.setter(this.recordPath, record, {
      params: {
        id,
      },
    });
  };

  setRecordStatus = (id: string, _rs: RecordStatus) => {
    this.platform.setter(this.recordStatusPath, _rs, {
      params: {
        id,
      },
    });
  };

  setOriginalRecord = (id: string, record: DBRow<T>) => {
    this.platform.setter(this.originalRecordIndexPath, record, {
      params: {
        id,
      },
    });
  };

  getRecord = (id: string): Row<T> => {
    const record = this.platform.getter(this.recordPath, {
      params: { id },
    }) as Row<T>;
    if (!record) {
      const ids = Object.keys(this.records());
      throw Error(
        `Invalid record id [${id}] passed! [${this.alias}]. ${
          ids.length
            ? `Available record ids are: ${JSON.stringify(ids)}`
            : 'Store is empty!'
        }`,
      );
    }
    return record;
  };

  getOriginalRecord = (id: string): DBRow<T> | undefined =>
    this.platform.getter(this.originalRecordIndexPath, { params: { id } });

  _removeRecord = (id: string) => {
    this.platform.remover(this.recordPath, {
      params: { id },
    });
  };

  removeOriginalRecord = (id: string) => {
    this.platform.remover(this.originalRecordIndexPath, {
      params: { id },
    });
  };

  removeRecordOrderId = (id: number) => {
    this.platform.remover(this.recordsOrderIndexPath, {
      params: { id },
    });
  };

  removeOriginalRecordOrderId = (oriIndex: number) => {
    let originalRecords = this.originalRecordsOrder();
    if (oriIndex >= 0) {
      originalRecords = originalRecords.filter(
        (_, index) => index !== oriIndex,
      );
      this.setOriginalRecordsOrder(originalRecords);
    }
  };

  selectedRecordIds = (): string[] => {
    return Object.keys(
      this.platform.getter(this.selectedRecordIdsPath) ?? EMPTY_OBJECT,
    );
  };

  selectedRecordsSize = () => {
    return this.selectedRecordIds().length;
  };

  selectRecord = (id: string) => {
    this.platform.setter([...this.selectedRecordIdsPath, id], true);
  };

  selectRecords = (ids: string[]) => {
    const selected = {
      ...(this.platform.getter<Record<string, boolean>>(
        this.selectedRecordIdsPath,
      ) ?? EMPTY_OBJECT),
    };
    ids.forEach((id) => {
      selected[id] = true;
    });
    this.platform.setter(this.selectedRecordIdsPath, selected);
  };

  isSelected = (id: string) => {
    return !!this.platform.getter([...this.selectedRecordIdsPath, id]);
  };

  hasRecord = (id: string) => {
    return this.recordsOrder().includes(id);
  };

  deSelectRecord = (id: string) => {
    this.platform.remover([...this.selectedRecordIdsPath, id]);
  };

  deSelectAll = () => {
    this.platform.setter(this.selectedRecordIdsPath, EMPTY_OBJECT);
  };

  selectAll = () => {
    const ids = this.recordsOrder();
    const selection = ids.reduce((map, id) => {
      map[id] = true;
      return map;
    }, {} as Record<string, boolean>);
    this.platform.setter(this.selectedRecordIdsPath, selection);
  };

  setBusy = (busy: boolean) => {
    this.platform.setter(this.isBusyPath, busy);
  };

  setInsertable = (insertable: boolean) => {
    if (Logger.isLogEnabled()) {
      Logger.log({
        itemType: this.ds,
        itemId: this.itemId,
        status: 'debug',
        msg: `Set Insertable: ${insertable}`,
      });
    }
    this.platform.setter(this.insertablePath, insertable);
  };

  isInsertable = () => {
    return this.platform.getter(this.insertablePath) ?? false;
  };

  setUpdatable = (updatable: boolean) => {
    if (Logger.isLogEnabled()) {
      Logger.log({
        itemType: this.ds,
        itemId: this.itemId,
        status: 'debug',
        msg: `Set Updatable: ${updatable}`,
      });
    }
    this.platform.setter(this.updatablePath, updatable);
  };

  isUpdatable = () => {
    return this.platform.getter(this.updatablePath) ?? false;
  };

  isReadonly = () =>
    !this.isUpdatable() && !this.isInsertable() && !this.isDeletable();

  setDeletable = (deletable: boolean) => {
    this.platform.setter(this.deletablePath, deletable);
  };

  isDeletable = (): boolean => {
    return this.platform.getter(this.deletablePath) ?? false;
  };

  setRequestID = (rid: string) => {
    this.requestId = rid;
    this.platform.setter(this.requestIDPath, rid);
  };

  getRequestID = (): string => {
    return this.platform.getter(this.requestIDPath) ?? '';
  };

  setInitialQueryFired = () => {
    this.platform.setQuietly(this.initialQueryFiredPath, true);
  };

  resetInitialQueryFired = () => {
    this.platform.setQuietly(this.initialQueryFiredPath, false);
  };

  hasInitialQueryFired = () => {
    return !!this.platform.getter(this.initialQueryFiredPath);
  };

  setAutoQueryFired = () => {
    this.platform.setQuietly(this.autoQueryFiredPath, true);
  };

  hasAutoQueryFired = () => {
    return !!this.platform.getter(this.autoQueryFiredPath);
  };

  isBusy = (): boolean => {
    return this.platform.getter(this.isBusyPath) ?? false;
  };

  getAttributes = async (): Promise<Attribute[]> => {
    return (
      this.ensureDsCache().getAttributes(
        this.appUid,
        this.ds,
        this.dsAppUid,
        this.cloudStore,
      ) ?? []
    );
  };

  getAttributesForDataSource = async (
    appUid: string,
    ds: string,
    dsAppUid: string | undefined,
  ): Promise<Attribute[]> => {
    return (
      this.ensureDsCache().getAttributes(
        appUid,
        ds,
        dsAppUid,
        this.cloudStore,
      ) ?? []
    );
  };

  getPkAttributesForDataSource = async (
    appUid: string,
    ds: string,
    dsAppUid: string | undefined,
  ): Promise<Attribute[]> => {
    return (
      this.ensureDsCache().getPrimaryKeyAttributes(appUid, ds, dsAppUid) ?? []
    );
  };

  getAttribute = (attr: string): Attribute | undefined => {
    return this.ensureDsCache().getAttribute(this.appUid, this.ds, attr);
  };

  getPrimaryKeyAttributes = async (): Promise<Attribute[]> => {
    return (
      this.ensureDsCache().getPrimaryKeyAttributes(
        this.appUid,
        this.ds,
        this.dsAppUid,
      ) ?? []
    );
  };

  getRecordsToPost = (dirtyRecords: Row<T>[]): Row<T>[] => {
    return dirtyRecords.map((record) => {
      const rec = { ...record };
      if (rec?._rs === 'U') {
        rec._changedAttributes = { ...rec._orig };
      }
      delete rec._orig;
      // delete rec._newKeys;
      return rec;
    });
  };

  dirtyRecords = (): Row<T>[] => {
    const dirtyRecords = this.getRecords().filter(
      (record: Row<T>) => record._rs !== 'Q' && record._rs !== 'N',
    );
    return dirtyRecords;
  };

  hasNewRecords = (): boolean => {
    const newRecord = this.getRecords().find((r) => r._rs === 'N');
    return !!newRecord;
  };

  createNew = async ({
    partialRecord,
    addOnTop = true,
    addAfter,
    addBefore,
    addBottom,
    cid,
    rs,
  }: {
    partialRecord: NewRow<T>;
    addOnTop?: boolean;
    addAfter?: string;
    addBefore?: string;
    addBottom?: boolean;
    cid?: string;
    rs: RecordStatus;
  }): Promise<string | undefined> => {
    const success = await invokeTrigger(this.beforeCreateTrigger, {
      new: partialRecord,
    });
    if (!success) {
      // trigger aborted
      return;
    }
    const defaultValues = {
      ...partialRecord,
      ...getActionSetValues(
        this.platform,
        this.appUid,
        this.pageId,
        this.itemId,
        partialRecord,
      ),
    };
    const _cid =
      cid || (this.ignorePKDuplicate ? `r${this._idCounter++}` : newRecordID());
    const _rs = rs || 'N';
    const newRec = { ...defaultValues, _cid, _rs } as NewRow<T>;
    await this.pubsub.pub('OnCreateNewRecord', { id: _cid, record: newRec });
    const newRecs = { ...this.records(), [_cid]: newRec };
    this.setRecords({ ...this.records(), [_cid]: newRec });
    let order = this.recordsOrder();
    // do not insert if the _cid already exists
    // this happens sometime during comment creation
    if (!order.includes(_cid)) {
      if (addAfter) {
        const index = order.indexOf(addAfter);
        if (index === -1) {
          throw new Error(
            `Cannot add record after ${addAfter} because it does not exist`,
          );
        }
        order = [...order.slice(0, index + 1), _cid, ...order.slice(index + 1)];
      } else if (addBefore) {
        const index = order.indexOf(addBefore);
        if (index === -1) {
          throw new Error(
            `Cannot add record after ${addBefore} because it does not exist`,
          );
        }
        order = [...order.slice(0, index), _cid, ...order.slice(index)];
      } else {
        if (addBottom || !addOnTop) order = [...order, _cid];
        else order = [_cid, ...order];
      }
      if (this.getTreeState()?.initialized) {
        let originalRecsOrder = this.originalRecordsOrder();
        originalRecsOrder = [_cid, ...originalRecsOrder];
        const initialExpaned = this.getTreeState().initialExpanded;
        const parentNodeIdAttribute = this.getTreeState().parentNodeIdAttribute;
        let parentId: string | undefined;
        const newRecParentNodeId = newRec[
          parentNodeIdAttribute
        ] as unknown as string;
        const rootRecords = order.filter((rid) => {
          const parentNodeId = newRecs[rid][
            parentNodeIdAttribute
          ] as unknown as string;
          const parentNodeRecordId = this.getByNodeId(parentNodeId, rid);
          if (parentNodeId === newRecParentNodeId) {
            parentId = parentNodeRecordId;
          }
          return !(
            parentNodeId &&
            parentNodeRecordId &&
            order.includes(parentNodeRecordId)
          );
        });
        for (let i = 0; i < rootRecords.length; i++) {
          this.addToTreeState(
            null,
            rootRecords[i],
            initialExpaned,
            newRecs,
            originalRecsOrder,
          );
        }
        const newRecordsOrder = this.makeNewTreeStateRecordsOrder(rootRecords);
        this.setOriginalRecordsOrder(originalRecsOrder);
        this.setRecordsOrder(newRecordsOrder);
        if (parentId && newRecParentNodeId) {
          this.expandTreeNode(parentId, newRecParentNodeId);
        }
        this.setCurrentRecordId(_cid);
        return _cid;
      } else {
        this.setRecordsOrder(order);
      }
    }
    this.setCurrentRecordId(_cid);
    return _cid;
  };

  _clearSync = () => {
    this.previousQuery = '';
    delete this.previousError;
    delete this.previousPromise;
    delete this.previousResult;
  };

  _clearQuietyly = async (makeBusy = false) => {
    this.setBusy(makeBusy);
    this.setCurrentRecordId(null);
    this.deSelectAll();
    this.setRecords({});
    this.setOriginalRecords({});
    this.setRecordsOrder([]);
    this.setOriginalRecordsOrder([]);
    this.platform.remover(this.errorMapPath);
    this.hasMore = false; // reset hasMore on clear to prevent grid fetching next page
  };

  clearQuietyly = async (makeBusy = false) => {
    this._clearSync();
    await this._clearQuietyly(makeBusy);
  };

  confirm = async (): Promise<boolean> => {
    const confirmed = await this.platform.confirm(
      'Discard Changes?',
      `Are you sure you want to discard the changes made to ${this.displayName}?`,
    );
    return confirmed;
  };

  clearWithConfirmation = async (): Promise<boolean> => {
    if (this.isStoreDirty() && !this.isReadonly()) {
      const confirmed = await this.confirm();
      if (!confirmed) {
        return false;
      }
      if (Logger.isLogEnabled()) {
        Logger.log({
          itemType: this.ds,
          itemId: this.itemId,
          status: 'warn',
          msg: `Performing clear with dirty rows: ${JSON.stringify(
            this.getDirtyRecordsForLog(),
          )}`,
        });
      }
    }
    await this.clear();
    return true;
  };

  clearAllCache = async () => {
    this._cache.clear();
    this._cacheReqBody.clear();
    await this.clear();
  };

  clear = async (makeBusy = false, beforeQuery = false) => {
    ReactDOM.unstable_batchedUpdates(() => {
      // this is causing state update error during render
      this.platform.forceUpdater(() => {
        if (!beforeQuery) {
          this._clearSync();
        }
        this._clearQuietyly(makeBusy);
      });
    });
    await this.pubsub.pub('OnStoreClear', undefined);
  };

  setError = (attribute: string, recordId: string, errorMessage: string) => {
    if (attribute && recordId) {
      this.platform.setter(this.errorPath, errorMessage, {
        params: {
          id: recordId,
          attribute,
        },
      });
    }
  };

  clearError = (attribute: string, recordId: string): void => {
    if (recordId && attribute) {
      this.platform.remover(this.errorPath, {
        params: {
          id: recordId,
          attribute,
        },
      });
    }
  };

  clearAllErrors = () => {
    this.hasErrors() && this.platform.remover(this.errorPath);
  };

  getError = (attribute: string, id: string): string | undefined => {
    return (
      id &&
      attribute &&
      this.platform.getter(this.errorPath, { params: { id, attribute } })
    );
  };

  getAllErrors = (): string | undefined => {
    const errors = (this.platform.getter(this.errorMapPath) ?? {}) as Record<
      string,
      Record<string, string>
    >;
    const recordIds = this.recordsOrder();
    const messages: string[] = [];
    Object.keys(errors).forEach((rId) => {
      const index = recordIds.indexOf(rId);
      if (index !== -1) {
        const msg: string[] = [];
        Object.keys(errors[rId]).forEach((attr) => {
          msg.push(errors[rId][attr]);
        });
        if (msg.length) {
          messages.push(`Row ${index + 1}: ${msg.join(', ')}`);
        }
      }
    });
    return messages.join('\n');
  };

  hasError = (attribute: string, id: string): boolean => {
    return !!this.getError(attribute, id);
  };

  hasErrors = (): boolean => {
    const errorMap: Record<
      string,
      Record<string, unknown>
    > = this.platform.getter(this.errorMapPath);
    const errors =
      errorMap &&
      Object.keys(errorMap).filter((key) => {
        return Object.keys(errorMap[`${key}`]).length > 0;
      });
    return errors && errors.length > 0;
  };

  getSmartSearchFilter = (): Filters<unknown> => {
    return this.platform.getter(this.smartSearchFilterPath) ?? [];
  };

  getGridFilter = (): Filters<unknown> => {
    return this.platform.getter(this.gridFilterPath) ?? [];
  };

  setSmartSeachFilter = (filters: Filters<unknown> | undefined) => {
    this.platform.setter(this.smartSearchFilterPath, filters);
  };

  setGridFilter = (filters: Filters<unknown> | undefined) => {
    this.platform.setter(this.gridFilterPath, filters);
  };

  readData = (): Records<T> => {
    if (this.previousError) {
      throw this.previousError;
    }
    if (!this.previousResult && this.previousPromise) {
      throw this.previousPromise;
    }
    return this.previousResult ?? {};
  };

  readPromise = (): Promise<boolean | void | Records<T>> => {
    if (this.previousError) {
      throw this.previousError;
    }
    if (this.previousPromise) {
      return this.previousPromise;
    }
    return resolvedPromise;
  };

  read = (queryRequest: QueryRequest<T>): Records<T> => {
    // filter must be default to empty array if not set in the props
    // as we are doing the same for this.previousQuery

    let props: QueryProps<T> = queryRequest.props ?? {
      filter: [],
      limit: this.maxRows,
      offset: 0,
    };

    let { filter = [], offset = 0, limit = this.maxRows, ...others } = props;

    const { force } = props;

    if (!queryRequest.skipApplyingFilters && this.hasInitialQueryFired()) {
      const ssFilter = this.getSmartSearchFilter();
      const gridFilter = this.getGridFilter();
      filter = [...filter, ...ssFilter, ...gridFilter];

      ({ props, filter } = applyActionFilters<T>(this, props, filter));
      ({ limit = this.maxRows, offset = 0, ...others } = props);
      filter = makeValid(filter, this.getAttribute);
    }

    const currentRquest = stringify({ ...others, filter, offset, limit });
    const _previousPromise = this.previousPromise;
    if (this.previousQuery !== currentRquest || !_previousPromise) {
      throw this.platform.quietCallback(async () => this.query(queryRequest));
    }
    const result = this.readData();
    if (force) {
      this.previousQuery = '';
    }
    return result;
  };

  getSort = (): SortType<DBRow<T>> | undefined => {
    return this.platform.getter(this.sortPath);
  };

  setSort = (sort: SortType<DBRow<T>>): SortType<DBRow<T>> => {
    return this.platform.setter(this.sortPath, sort);
  };

  sort = async (sort: SortType<DBRow<T>>) => {
    if (this.isStoreDirty() && !this.isReadonly()) {
      if (Logger.isLogEnabled()) {
        Logger.log({
          itemType: this.ds,
          itemId: this.itemId,
          status: 'warn',
          msg: `Performing sort with dirty rows: ${stringify(
            this.getDirtyRecordsForLog(),
          )}`,
        });
      }
      const confirmed = await this.confirm();
      if (!confirmed) {
        return;
      }
      this.reset();
    }

    this.setSort(sort);
    if (this.filterLocally) {
      this.applyLocalSort(sort);
      return;
    }
    const queryProps: QueryProps<T> = JSON.parse(this.previousQuery || '{}');
    queryProps.sort = sort;
    queryProps.offset = 0;

    return this.query({ props: queryProps, skipApplyingFilters: true });
  };

  _localSort(recordIds: string[], sort: SortType<DBRow<T>>): string[] {
    const key = Object.keys(sort)[0] as keyof T;
    if (key) {
      const direction = (sort[key as keyof typeof sort] ?? 0) > 0 ? 1 : -1;
      const records = this.records();
      recordIds.sort((a, b) => {
        const l = records[a][key];
        const r = records[b][key];
        if (l === r) return 0;
        let compare = 0;
        if (isNull(l)) {
          compare = 1;
        } else if (isNull(r)) {
          compare = -1;
        } else {
          compare = l < r ? -1 : 1;
        }
        return compare * direction;
      });
    }
    return recordIds;
  }

  applyLocalSort = (sort: SortType<DBRow<T>>) => {
    const ids = this._localSort([...this.recordsOrder()], sort);
    this.setRecordsOrder(ids);
    // reset the current record to the top most after applying sort
    const currentRecordId = this.recordsOrder()[0];
    this.setCurrentRecordId(currentRecordId);
  };

  next = async () => {
    const queryProps: QueryProps<T> = JSON.parse(this.previousQuery || '{}');
    if (this.filterLocally && !this.ds.startsWith('__ds__')) {
      this.platform.showWarning(
        `Calling next() is not allowed on a locally filtered store! Consider increasing query limit [Store: ${this.ds}.${this.alias}]`,
        { appUid: this.appUid, pageId: this.pageId, itemId: this.itemId ?? '' },
      );
      return;
    }
    const nextOffset = (queryProps.offset || 0) + this.maxRows;
    queryProps.offset = nextOffset;
    return this.query({
      props: queryProps,
      noClear: true,
      skipApplyingFilters: true,
    });
  };

  clearPreviousRequestCache = (): void => {
    this._cache.delete(this.previousQuery);
  };

  queryWithRequest = async ({
    currentRquest,
    reqBody,
    noClear = false,
    force = false,
    batchCallback = emptyFunction,
    parallelQuery = false,
  }: {
    currentRquest: string;
    reqBody: RootRequest<T>;
    noClear?: boolean;
    force?: boolean;
    batchCallback?: () => void;
    parallelQuery?: boolean;
  }): Promise<void | Records<T>> => {
    // console.log('in queryWithRequest', this.alias);
    if (!noClear) await this.clear(true, true);

    const rows =
      !force && this.cacheAll ? this._cache.get(currentRquest) : undefined;
    if (rows) {
      batchCallback();
      this.previousQuery = currentRquest;
      this.previousQueryBody = this._cacheReqBody.get(currentRquest);
      return this.processServerResponse(rows);
    }
    if (!this.platform.sendRequest) {
      throw Error('Missing platform.sendRequest!');
    }
    this.setBusy(true);
    this.platform.stateXScheduler();
    this.previousQueryBody = reqBody;

    if (this.cacheAll) {
      this._cacheReqBody.set(currentRquest, reqBody);
    }

    let promise: Promise<WSResponse | Response>;
    let rid = '';
    let timestamp = 0;
    if (this.itemType === 'CloudDataStore') {
      timestamp = new Date().getTime();
      rid = nextCloudRequestId();
      promise = fetch(
        `${getUrl()}fn/${this.dsAppUid || this.appUid}/${
          this.ds
        }?${getURLQueryStringToPost(this.platform.getter(userAtom))}`,
        {
          credentials: 'include',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-role-uid': this.platform.roleUid,
          },
          body: stringify({
            method: 'LOAD',
            data: reqBody[this.alias].query?.data,
            filters: reqBody[this.alias].query?.filter,
          }),
        },
      );
    } else {
      [promise, rid] = this.platform.sendRequest(
        {
          type: 'Post',
          data: reqBody,
          system: this.programmatic,
        },
        {
          appUid: this.dsAppUid || this.appUid,
          pageUid: this.pageId,
          roleUid: this.platform.roleUid,
        },
      );
    }
    this.setRequestID(rid);

    if (Logger.isLogEnabled()) {
      Logger.log({
        itemType: this.ds,
        itemId: this.itemId,
        status: 'info',
        msg: `${rid} Query: ${JSON.stringify(reqBody)}`,
      });
    }

    const initializeAttributesPromise = this.initializeAttributes();
    batchCallback();

    return promise
      .then(async (resp) => {
        let serverResponse: WSResponse;
        if (resp instanceof Response) {
          const json = await resp.json();
          const data: DataResponse<T> = Array.isArray(json)
            ? {
                data: json.map((r) => ({ ...r, _rs: 'Q' })),
                rowsFetched: json.length,
              }
            : json;

          serverResponse =
            resp.status >= 200 && resp.status < 300 && Array.isArray(json)
              ? {
                  data: {
                    [this.alias]: data,
                  },
                  uid: rid,
                  timestamp,
                  status: 'success',
                }
              : {
                  status: 'error',
                  message: json.message ?? JSON.stringify(json),
                  uid: rid,
                  timestamp,
                  title: 'Unexpected Error',
                };
        } else {
          serverResponse = resp;
        }
        // await for initializeAttributes here to prevent multiple calls due to store.read
        // as it looks for the previousPromise
        await initializeAttributesPromise;
        const { uid } = serverResponse;
        const currentRequestID = this.getRequestID();
        if (currentRequestID !== uid && !parallelQuery) {
          console.warn(
            `Ignoring stale request ${this.alias}.${uid} in favor of ${currentRequestID}`,
          );
          return;
        }
        if (hasWSError(serverResponse)) {
          this.platform.showError(serverResponse.message);
          this.previousError = new IOError(
            'Fetch Error',
            serverResponse.message,
          );
          this.hasMore = false;
          this.previousResult = {};

          if (Logger.isLogEnabled()) {
            Logger.log({
              itemType: this.ds,
              itemId: this.itemId,
              status: 'info',
              msg: `${rid} Query Error: ${serverResponse.message}`,
            });
          }

          return;
        }
        const data = serverResponse.data as {
          [dsAlias: string]: DataResponse<T>;
        };
        if (data[this.alias].pagination) {
          this.pagination = data[this.alias].pagination;
        } else {
          delete this.pagination;
        }
        this.setTotalRowCount(data[this.alias].totalRowCount);
        const _rows = data[this.alias].data;
        if (this.cacheAll) {
          this._cache.set(currentRquest, _rows);
        }
        this.setBusy(false);
        const records = await this.processServerResponse(_rows);
        await this.pubsub.pub('OnAfterQuery', { rows: _rows, records });

        if (Logger.isLogEnabled()) {
          Logger.log({
            itemType: this.ds,
            itemId: this.itemId,
            status: 'info',
            msg: `${rid} Query Response: ${_rows.length} rows`,
          });
        }

        return records;
      })
      .catch((e) => {
        this.hasMore = false;
        this.previousResult = {};

        if (Logger.isLogEnabled()) {
          Logger.log({
            itemType: this.ds,
            itemId: this.itemId,
            status: 'info',
            msg: `${rid} Query Error: ${getErrorMessage(e)}`,
          });
        }
      })
      .finally(() => {
        this.setBusy(false);
      });
  };

  refresh = async (force = false): Promise<void | Records<T> | undefined> => {
    if (this.previousQueryBody && this.previousQuery) {
      if (!force && this.isStoreDirty() && !this.isReadonly()) {
        if (Logger.isLogEnabled()) {
          Logger.log({
            itemType: this.ds,
            itemId: this.itemId,
            status: 'warn',
            msg: `Performing refresh with dirty rows: ${stringify(
              this.getDirtyRecordsForLog(),
            )}`,
          });
        }
        const confirmed = await this.confirm();
        if (!confirmed) {
          return;
        }
        this.reset();
      }
      let mergedData = {};
      let mergedFilters: Filters<T> = [];
      if (this.pageId !== '$$Sidebar$$') {
        let triggerBeforeQueryItems: Item<'TriggerBeforeQuery'>[] = [];
        triggerBeforeQueryItems = this.platform.getChildrenItemsById(
          this.appUid,
          this.pageId,
          this.itemId as string,
          (child) =>
            (child.type === 'TriggerBeforeQuery' ||
              child.type === 'TriggerBeforeLoad') &&
            isTriggerOrActionEnabled(
              child,
              this.platform,
              this.pageId,
              this.appUid,
            ),
        ) as Item<'TriggerBeforeQuery'>[];
        let beforeQueryFilters = {};
        let beforeQueryData = {};
        if (triggerBeforeQueryItems.length) {
          const len = triggerBeforeQueryItems.length;
          for (let i = 0; i < len; i++) {
            const item = triggerBeforeQueryItems[i];
            const { filter, data } = getFilterFromActions({
              appUid: this.appUid,
              platform: this.platform,
              store: this,
              pageId: this.pageId,
              itemId: item.itemId,
              extraProps: {},
              showEmptyFilterWarning: false,
            });
            if (Object.keys(filter).length) {
              beforeQueryFilters = {
                ...beforeQueryFilters,
                ...filter,
              };
            }
            if (Object.keys(data).length) {
              beforeQueryData = {
                ...beforeQueryData,
                ...data,
              };
            }
          }
        }
        const baseFilters = this.previousQueryBody[this.alias].query?.filter;
        const safeBaseFilters = baseFilters ?? [];
        const entries = Object.values(beforeQueryFilters ?? {});
        mergedFilters = [...safeBaseFilters];
        for (const filter of entries) {
          if (!filter || typeof filter !== 'object') continue;
          const newKey = Object.keys(filter)[0];
          if (!newKey) continue;
          const index = mergedFilters.findIndex(
            (f) => Object.keys(f)[0] === newKey,
          );
          if (index !== -1) {
            mergedFilters[index] = filter;
          } else {
            mergedFilters.push(filter);
          }
        }
        mergedData = {
          ...this.previousQueryBody[this.alias].query?.data,
          ...beforeQueryData,
        };
      }
      const req = JSON.parse(this.previousQuery) as QueryProps<T>;
      // req.offset = 0;
      const currentQuery = stringify(req);
      const reqBody: RootRequest<T> = {
        [this.alias]: {
          ...this.previousQueryBody[this.alias],
          query: {
            ...this.previousQueryBody[this.alias].query,
            offset: this.hasPagination ? req.offset : 0,
            ...(this.pageId !== '$$Sidebar$$'
              ? { filter: mergedFilters, data: mergedData }
              : {}),
          },
        },
      };

      this._clearSync();

      this.previousQuery = currentQuery;
      this.previousPromise = new Promise((resolve, reject) => {
        return this.queryWithRequest({
          currentRquest: currentQuery,
          reqBody,
          noClear: false,
          force: true,
        })
          .then((result) => {
            resolve(result);
          })
          .catch((error) => {
            this.setBusy(false);
            // console.error(error);
            // this.platform.showError(`Error while fetching data ${error}`);
            if (error instanceof AbortError) {
              this.previousResult = {};
              resolve(undefined);
            } else {
              this.previousError = error;
              reject(error);
            }
          });
      });
      return this.previousPromise;
    }
  };

  static dsCache: DSAttributesCache | null = null;

  ensureDsCache = (): DSAttributesCache => {
    if (Store.dsCache === null) {
      Store.dsCache = new DSAttributesCache({
        set: this.platform.setter,
        get: this.platform.getter,
        platform: this.platform,
      });
    } else if (Store.dsCache.platform !== this.platform) {
      // happens when navigating to /help and back
      Store.dsCache.platform = this.platform;
    }
    return Store.dsCache;
  };

  static destroy = () => {
    Store.dsCache = null;
    DSAttributesCache.destroy();
  };

  refreshAttributes = async (): Promise<boolean> => {
    return this.ensureDsCache().refreshAttributes(
      this.appUid,
      this.ds,
      this.dsAppUid,
    );
  };

  initializeLocalDataStoreAttributes = async () => {
    if (this.itemId) {
      const item = this.platform.getItem(this.appUid, this.pageId, this.itemId);
      const {
        data: csvData,
        jsonData,
        tsData,
        dataFormat,
      } = (item as LocalDataStoreItem)?.props;
      const data =
        dataFormat === 'csv'
          ? csvData
          : dataFormat === 'json'
          ? jsonData
          : tsData;
      this.data.data = data;
      // await this.clear();
      if (!isEmpty(data)) {
        const { attrs } =
          dataFormat === 'csv'
            ? parseCSV({
                platform: this.platform,
                appUid: this.appUid,
                pageId: this.pageId,
                itemId: this.itemId,
                data,
              })
            : dataFormat === 'json'
            ? parseJSON(this.appUid, data)
            : await parseTSData({
                appUid: this.appUid,
                data: preCompile(tsData ?? ''),
                platform: this.platform,
                pageId: this.pageId,
                itemId: this.itemId,
              });

        this.platform.setter<Attribute[]>(
          attributesPath(this.appUid, this.ds),
          attrs,
        );
        this.platform.setter<Attribute[]>(
          pkAttributesPath(this.appUid, this.ds),
          attrs.filter((a) => a.primaryKey === 'Y'),
        );
        DSAttributesCache.setInitializedAttributes(this.appUid, this.ds);
      }
    }
  };

  initializeAttributes = async (): Promise<boolean> => {
    if (this.itemType === 'LocalDataStore') {
      await this.initializeLocalDataStoreAttributes();
      return true;
    } else if (this.itemType === 'CloudDataStore') {
      return true;
    }
    return this.ensureDsCache().initializeAttributes(
      this.appUid,
      this.ds,
      this.dsAppUid,
    );
  };

  initialQueryTimeout: NodeJS.Timeout | null = null;

  initialPromise: Promise<void | Records<T>> | null = null;

  initialQuery: {
    queryRequest: QueryRequest<T>;
    resolve: (result: Records<T>) => void;
    reject: (e: unknown) => void;
  } = { queryRequest: {}, resolve: emptyFunction, reject: emptyFunction };

  _lastQuery = 0;

  getDirtyRecordsForLog = (): Record<string, unknown>[] => {
    return this.dirtyRecords().map((r) => {
      const { _rs, _orig, _newKeys, _id, _cid } = r;
      const rec: Record<string, unknown> = {
        _rs,
        _orig,
        _newKeys,
        _id,
        _cid,
      };
      _newKeys?.forEach((key) => {
        // @ts-ignore dynamic key
        rec[key] = r[key];
      });
      if (_orig) {
        Object.keys(_orig).forEach((key) => {
          // @ts-ignore dynamic key
          rec[key] = r[key];
        });
      }
      return rec;
    });
  };

  progressCounter = 0;
  query = async (
    queryRequest: QueryRequest<T> = {},
  ): Promise<void | Records<T>> => {
    const end = startPageProgress(
      this.appUid,
      this._pageId,
      `${this.itemId} (${this.alias}) ${++this.progressCounter}`,
    );
    return this.__query(queryRequest).finally(end);
  };

  __query = async (
    queryRequest: QueryRequest<T> = {},
  ): Promise<void | Records<T>> => {
    if (
      !queryRequest.props?.force &&
      this.isStoreDirty() &&
      !this.isReadonly()
    ) {
      if (Logger.isLogEnabled()) {
        Logger.log({
          itemType: this.ds,
          itemId: this.itemId,
          status: 'warn',
          msg: `Performing query with dirty rows: ${JSON.stringify(
            this.getDirtyRecordsForLog(),
          )}`,
        });
      }
      const confirmed = await this.confirm();
      if (!confirmed) {
        queryRequest.cancelBatch?.();
        return;
      }
      this.reset();
    }
    return this._query(queryRequest);
  };

  _query = async (
    queryRequest: QueryRequest<T>,
  ): Promise<void | Records<T>> => {
    if (!this.localStore) {
      await this.initializeAttributes();
    }
    let props: QueryProps<T> = queryRequest.props ?? {
      filter: [],
      limit: this.maxRows,
      offset: 0,
    };
    const noClear = queryRequest.noClear ?? false;
    const batchCallback = queryRequest.batchCallback ?? emptyFunction;

    if (this.localStore && !this.hasInitialQueryFired()) {
      return;
    }

    if (this.filterLocally && this.hasInitialQueryFired()) {
      this.applyLocalFilters(queryRequest.props?.filter);
      return;
    }

    await this.pubsub.pub('OnBeforeQuery', { queryProps: props });
    if (props.abortMessage) {
      if (props.abortMessage !== 'SILENT_ABORT')
        this.platform.showError(props.abortMessage);
      return;
    }

    let {
      filter = [],
      force = false,
      limit = this.maxRows,
      offset = 0,
      parallelQuery = false,
      ...others
    } = props;

    if (!queryRequest.skipApplyingFilters) {
      const ssFilter = this.getSmartSearchFilter();
      const gridFilter = this.getGridFilter();
      filter = [...filter, ...ssFilter, ...gridFilter];

      ({ props, filter } = applyActionFilters<T>(this, props, filter));
      ({
        force = false,
        limit = this.maxRows,
        offset = 0,
        parallelQuery = false,
        ...others
      } = props);
      filter = makeValid(filter, this.getAttribute, {
        itemId: this.itemId ?? '',
      });
    }
    let sort = props.sort;
    if (!sort || Object.keys(sort).length === 0) {
      sort = this.getSort();
    }
    const _cr = { ...props, sort, filter, limit, offset };
    const currentRquest = stringify(_cr);

    const success = await invokeTrigger(this.beforeQueryTrigger, {
      request: _cr,
    });
    if (!success) {
      // query aborted
      return;
    }

    if (
      !force &&
      this.previousQuery === currentRquest &&
      this.previousPromise
    ) {
      batchCallback();
      if (Logger.isLogEnabled()) {
        Logger.log({
          itemType: this.ds,
          itemId: this.itemId,
          status: 'query',
          msg: `Duplicate query (same as ${
            this.getRequestID() || 'inprogress'
          }). Hence skipping: ${currentRquest}`,
        });
      }
      return this.previousPromise;
    }

    const now = performance.now();
    if (this._lastQuery && now - this._lastQuery < 300) {
      if (!force && this.cacheAll && !!this._cache.get(currentRquest)) {
        // result already cached... so it's ok
      } else {
        let offsetChanged = false;
        let previousOffset = 0;
        if (this.previousQuery) {
          const previousQ = JSON.parse(this.previousQuery);
          previousOffset = previousQ.offset;
          if (previousOffset !== offset) {
            offsetChanged = true;
          }
        }

        if (
          (isDeveloper(this.platform, this.appUid) ||
            isAdministrator(this.platform, this.appUid)) &&
          !this.doNotMonitorQueryRequest
        ) {
          this.platform.showMessage({
            msg: `Multiple queries getting fired for alias ${this.alias}. ${
              offsetChanged
                ? `Offset changed from ${previousOffset} to ${offset} between queries. You might want to increase the Max Rows property from ${limit} to ${Math.max(
                    limit * 2,
                    100,
                  )}.`
                : 'Check debug console for more details.'
            }`,
            type: 'warning',
            item: this.itemId
              ? {
                  appUid: this.appUid,
                  pageId: this.pageId,
                  itemId: this.itemId,
                }
              : undefined,
          });
        }
        if (Logger.isLogEnabled()) {
          Logger.log({
            itemType: this.ds,
            itemId: this.itemId,
            status: 'warn',
            msg: `Multiple queries within ${Math.round(
              now - this._lastQuery,
            )}ms [${this.alias}], PreviousQuery: ${
              this.previousQuery
            }, CurrentQuery: ${currentRquest}`,
          });
        }
      }
    }
    this._lastQuery = now;

    this.setInitialQueryFired();

    this._clearSync();

    this.previousQuery = currentRquest;
    this.previousPromise = new Promise((resolve, reject) => {
      const reqBody: RootRequest<T> = {
        [this.alias]: {
          ds: this.ds,
          query: {
            ...others,
            includeRowCount: props.includeRowCount ?? this.hasPagination,
            filter,
            limit,
            offset,
            sort,
            pagination: this.pagination,
          },
        },
      };
      if (this.maskPageOnQuery) maskPageSignal.queryCount.set((v) => v + 1);
      return this.queryWithRequest({
        currentRquest,
        reqBody,
        noClear,
        force,
        batchCallback,
        parallelQuery,
      })
        .then((result) => {
          resolve(result);
        })
        .catch((error) => {
          this.setBusy(false);
          if (error instanceof AbortError) {
            this.previousResult = {};
            resolve(undefined);
          } else {
            this.previousError = error;
            reject(error);
          }
        })
        .finally(() => {
          if (this.maskPageOnQuery) maskPageSignal.queryCount.set((v) => v - 1);
        });
    });
    return this.previousPromise;
  };

  applyLocalFilters = async (
    filter: Filters<T> | undefined = [],
  ): Promise<void> => {
    const localFilter = [
      ...filter,
      ...this.getGridFilter(),
      ...this.getSmartSearchFilter(),
    ];
    if (Logger.isLogEnabled()) {
      Logger.log({
        itemType: this.ds,
        itemId: this.itemId,
        status: 'info',
        msg: `Applying local filter: ${JSON.stringify(localFilter)}`,
      });
    }
    const attributes = await this.getAttributes();

    if (!this.platform.isStateXMounted()) {
      // ClassName.test.tsx
      return;
    }

    // console.log('applying local filters', localFilter);
    let recordsOrder = this.originalRecordsOrder().filter((recid) =>
      applyFilters(
        localFilter,
        'allOf',
        {
          name: this.alias,
          attributes,
        },
        this.records()[recid],
      ),
    );
    const sort = this.getSort();
    if (sort && Object.keys(sort).length > 0) {
      recordsOrder = this._localSort(recordsOrder, sort);
    }
    this.setRecordsOrder(recordsOrder);
    // reset the current record to the top most after applying filters
    const currentRecordId = recordsOrder.length ? recordsOrder[0] : undefined;
    this.setCurrentRecordId(currentRecordId);
  };

  replaceClientId = (
    clientId: string,
    newId: string,
    postedServerRecord: DBRow<T>,
    originalRecord?: DBRow<T>,
  ) => {
    ReactDOM.unstable_batchedUpdates(() => {
      const recordsOrder = updateValue(this.recordsOrder(), clientId, newId);
      this.setRecordsOrder(recordsOrder);

      if (this.isSelected(clientId)) {
        this.deSelectRecord(clientId);
        this.selectRecord(newId);
      }

      const newRecords: Records<T> = replaceKeyAndValue(
        clientId,
        newId,
        postedServerRecord,
        this.records(),
      );
      this.setRecords(newRecords);
      if (this.getCurrentRecordId() === clientId) {
        this.setCurrentRecordId(newId, true);
      }
      // to handle insert case when server delay and user has changed value before receiving res
      if (postedServerRecord._rs === 'U' && originalRecord) {
        const newOriginalRecords: Records<T> = replaceKeyAndValue(
          clientId,
          newId,
          originalRecord,
          this.originalRecords(),
        );
        this.setOriginalRecords(newOriginalRecords);
      }
    });
  };

  serverValidate = async <K extends keyof T>({
    attribute,
    oldValue,
  }: {
    attribute: K;
    oldValue?: T[K];
  }): Promise<boolean> => {
    if (this.transient) {
      this.platform.showError(
        `Transient Store [${this.alias}] cannot be validated!`,
      );
      return false;
    }
    await this.initializeAttributes();
    if (this.isBusy()) {
      this.platform.showError('Store is busy... try again later!');
      return false;
    }
    const record = this.getCurrentRecord();
    if (!record) {
      this.platform.showError(
        `Missing current record! Validation of ${String(attribute)} aborted.`,
      );
      return true;
    }
    if (this.maskPageOnPost) maskPageSignal.postCount.set((v) => v + 1);
    if (isNull(oldValue) && record._orig) {
      oldValue = record._orig[attribute] as T[K];
    }

    const request: DataRequest<T> = {
      ds: this.ds,
      data: [{ ...record, _rs: 'V', _ca: attribute as string, _ov: oldValue }],
    };

    this.setBusy(true);
    const [promise, requestId] = this.platform.sendRequest(
      {
        type: 'Post',
        data: { [this.alias]: request },
      },
      {
        appUid: this.dsAppUid || this.appUid,
        pageUid: this.pageId,
        roleUid: this.platform.roleUid,
      },
    );

    if (Logger.isLogEnabled()) {
      Logger.log({
        itemType: this.ds,
        itemId: this.itemId,
        status: 'info',
        msg: `${requestId} Validate: ${JSON.stringify(request)}`,
      });
    }
    const result = await promise
      .then(async (serverResponse) => {
        if (hasWSError(serverResponse)) {
          this.platform.showError(serverResponse.message);
          return false;
        }
        const responseData = serverResponse.data[this.alias].data as DBRow<T>[];
        await this.populateId(responseData);

        ReactDOM.unstable_batchedUpdates(() => {
          const id = record._cid || record._id;
          if (id) {
            let currentRecord = this.records()[id];
            const postedServerRecord = responseData.find((r) => {
              return (r._cid && r._cid === id) || (r._id && r._id === id);
            }) as DBRow<T>;
            const { _ca, _ov, ...others } = postedServerRecord;
            if (currentRecord._rs === 'U') {
              currentRecord = this.processUpdateRecord(id, postedServerRecord);
            }
            // @ts-ignore _rs can be I
            others._rs = currentRecord._rs;
            if (record === currentRecord) {
              this.setRecord(id, others);
            } else {
              // user has changed data after send validation request before receiving server response
              const newRecord = { ...others, ...currentRecord };
              this.setRecord(id, newRecord);
            }
          }
        });
        return true;
      })
      .catch((error) => {
        // platform.showError is taken care in useWebSocket
        // this.platform.showError(`Error while posting data ${error}`);
        return false;
      })
      .finally(() => {
        this.setBusy(false);
        if (this.maskPageOnPost) maskPageSignal.postCount.set((v) => v - 1);
      });
    return result;
  };

  save = async (
    {
      silent = false,
      feedback = this.feedback,
      batchCallback,
      cancelBatch,
    }: {
      silent?: boolean;
      feedback?: 'NONE' | string;
      batchCallback?: () => void;
      cancelBatch?: () => void;
    } = { silent: false },
  ): Promise<boolean> => {
    if (this.transient) {
      this.platform.showError(
        `Transient Store [${this.alias}] cannot be saved!`,
      );
      cancelBatch?.();
      return false;
    }
    await this.initializeAttributes();
    if (this.isBusy()) {
      this.platform.showError('Store is busy... try again later!');
      cancelBatch?.();
      return false;
    }
    const dirtyRecords = this.dirtyRecords().filter((r) => r._rs !== 'N');
    if (dirtyRecords.length === 0) {
      if (!silent) this.platform.showWarning('No changes to post!');
      batchCallback?.();
      return true;
    }

    if (this.hasErrors()) {
      this.platform.showError(
        `You must resolve all the validation errors before submiting it.

${this.getAllErrors()}
`,
      );
      cancelBatch?.();
      return false;
    }

    if (Logger.isLogEnabled()) {
      Logger.log({
        itemType: this.ds,
        itemId: this.itemId,
        status: 'info',
        msg: `Posting dirty rows: ${stringify(this.getDirtyRecordsForLog())}`,
      });
    }
    if (this.maskPageOnPost) maskPageSignal.postCount.set((v) => v + 1);
    this.setBusy(true);

    await this.handleTriggerBeforeDataStoreSave(dirtyRecords, cancelBatch);

    const request: DataRequest<T> = {
      ds: this.ds,
      data: this.getRecordsToPost(dirtyRecords),
    };

    await this.pubsub.pub('OnBeforePost', { request });

    let promise: Promise<WSResponse | Response>;
    let requestId = '';
    let timestamp = 0;
    if (this.itemType === 'CloudDataStore') {
      timestamp = new Date().getTime();
      requestId = nextCloudRequestId();
      promise = fetch(
        `${getUrl()}fn/${this.dsAppUid || this.appUid}/${
          this.ds
        }?${getURLQueryStringToPost(this.platform.getter(userAtom))}`,
        {
          credentials: 'include',
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-role-uid': this.platform.roleUid,
          },
          body: stringify({
            method: 'POST',
            data: dirtyRecords,
          }),
        },
      );
    } else {
      [promise, requestId] = this.platform.sendRequest(
        {
          type: 'Post',
          data: { [this.alias]: request },
        },
        {
          appUid: this.dsAppUid || this.appUid,
          pageUid: this.pageId,
          roleUid: this.platform.roleUid,
        },
      );
    }

    if (Logger.isLogEnabled()) {
      Logger.log({
        itemType: this.ds,
        itemId: this.itemId,
        status: 'info',
        msg: `${requestId} Save: ${JSON.stringify(request)}`,
      });
    }
    batchCallback?.();
    const result = await promise
      .then(async (resp) => {
        try {
          let serverResponse: WSResponse;
          if (resp instanceof Response) {
            const json = await resp.json();
            let data: DataResponse<T> = json;
            if (Array.isArray(json)) {
              if (json.length !== dirtyRecords.length) {
                this.platform.showError(
                  `Developer Error: Cloud function ${this.ds} returned ${json.length} rows when posting ${dirtyRecords.length} rows!`,
                );
                return false;
              }
              dirtyRecords.forEach((r, index) => {
                delete json[index]._orig;
                json[index] = {
                  ...json[index],
                  _cid: r._cid,
                  _id: r._id,
                  _rs: r._rs === 'D' ? r._rs || 'D' : 'Q',
                };
              });
              data = {
                data: json,
                rowsFetched: json.length,
                queryElapsed: new Date().getTime() - timestamp,
                totalRowCount: json.length,
              };
            }

            serverResponse =
              resp.status >= 200 && resp.status < 300 && Array.isArray(json)
                ? {
                    data: {
                      [this.alias]: data,
                    },
                    uid: requestId,
                    timestamp,
                    status: 'success',
                  }
                : {
                    status: 'error',
                    message: json.message ?? JSON.stringify(json),
                    uid: requestId,
                    timestamp,
                    title: 'Unexpected Error',
                  };
          } else {
            serverResponse = resp;
          }

          if (hasWSError(serverResponse)) {
            this.platform.showError(serverResponse.message);
            return false;
          }
          const responseData = serverResponse.data[this.alias]
            .data as DBRow<T>[];

          const _responseData: DBRow<T>[] = JSON.parse(
            JSON.stringify(responseData),
          );

          const statusData: {
            insertIds: string[];
            updateIds: string[];
            deleteIds: string[];
          } = { insertIds: [], updateIds: [], deleteIds: [] };

          dirtyRecords.forEach((r) => {
            const id = r._id ?? r._cid;
            if (id) {
              if (r._rs === 'I') {
                statusData.insertIds.push(id);
              } else if (r._rs === 'U') {
                statusData.updateIds.push(id);
              } else if (r._rs === 'D') {
                statusData.deleteIds.push(id);
              }
            }
          });

          if (
            dirtyRecords.length > 20 &&
            !dirtyRecords.find((r) => r._rs !== 'I') &&
            dirtyRecords.length === this.getSize() &&
            dirtyRecords.length === responseData.length
          ) {
            // all records are new and we have the same number of records in the response as we sent
            // so we can assume that the response is in the same order as the request
            // and we can replace the records in the store with the response
            // this is a performance optimization - bulk upload case
            await this.clear();
            await this.processServerResponse(responseData);
          } else {
            await this.populateId(responseData);
            if (this.getTreeState()?.initialized) {
              // Need to update the tree state with the newly nodeAttributeId for the response records
              await this.updateTreeStateRecords(responseData);
            }
            ReactDOM.unstable_batchedUpdates(() => {
              dirtyRecords.forEach((dirtyRecord) => {
                const id = dirtyRecord._cid || dirtyRecord._id;
                if (id) {
                  const currentRecord = this.records()[id];
                  if (dirtyRecord._rs === 'D') {
                    this.deleteFromStore(id);
                  } else {
                    const postedServerRecord = responseData.find((record) => {
                      return (
                        (record._cid && record._cid === id) ||
                        (record._id && record._id === id)
                      );
                    }) as DBRow<T>;
                    const clientId = postedServerRecord._cid;
                    const newId = postedServerRecord._id;
                    delete postedServerRecord._cid;
                    if (dirtyRecord === currentRecord) {
                      if (dirtyRecord._cid && newId && clientId) {
                        this.replaceClientId(
                          clientId,
                          newId,
                          postedServerRecord,
                        );
                      } else {
                        this.setRecord(id, postedServerRecord);
                        this.removeOriginalRecord(id);
                      }
                    } else {
                      // user has changed data after hitting save btn before receiving server response
                      const newRecord = { ...postedServerRecord };
                      const _orig = currentRecord?._orig;
                      if (_orig) {
                        newRecord._orig = { ..._orig };
                        (Object.keys(_orig) as (keyof T)[]).forEach((key) => {
                          if (newRecord[key] !== currentRecord[key]) {
                            newRecord._rs = 'U';
                            // @ts-ignore fix type issue
                            newRecord[key] = currentRecord[key];
                            if (newRecord._orig) {
                              // @ts-ignore fix type issue
                              newRecord._orig[key] = postedServerRecord[key];
                            }
                          }
                        });
                        this.setRecord(id, newRecord);
                        this.setOriginalRecord(id, postedServerRecord);
                      }
                      if (dirtyRecord._rs === 'I') {
                        newRecord._orig = {};
                        (Object.keys(dirtyRecord) as (keyof T)[]).forEach(
                          (key) => {
                            if (dirtyRecord[key] !== currentRecord[key]) {
                              newRecord._rs = 'U';
                              // @ts-ignore fix type issue
                              newRecord[key] = currentRecord[key];
                              if (newRecord._orig) {
                                // @ts-ignore fix type issue
                                newRecord._orig[key] = postedServerRecord[key];
                              }
                            }
                          },
                        );
                        if (clientId && newId && newRecord) {
                          this.replaceClientId(
                            clientId,
                            newId,
                            newRecord,
                            postedServerRecord,
                          );
                        }
                      }
                    }
                  }
                }
              });
            });
          }
          if (feedback !== 'NONE') {
            const msg = feedback
              ? feedback.replace('#{count}', `${responseData.length}`)
              : `${responseData.length} records posted`;

            this.platform.showSuccess(msg);
          }
          this.handleTriggerAfterDataStoreSave(_responseData, statusData);
          return true;
        } catch (e) {
          console.error(e);
          this.platform.showError(
            `Unexpected error while posting data: ${getErrorMessage(e)}`,
          );
          return false;
        }
      })
      .catch((error) => {
        // platform.showError is taken care in useWebSocket
        // this.platform.showError(`Error while posting data ${error}`);
        return false;
      })
      .finally(() => {
        if (this.maskPageOnPost) maskPageSignal.postCount.set((v) => v - 1);
        this.setBusy(false);
      });
    return result;
  };

  handleTriggerBeforeDataStoreSave = async (
    dirtyRecords: Row<T>[],
    cancelBatch?: () => void,
  ) => {
    let status = true;
    try {
      status = await invokeTrigger(this.beforeSaveTrigger).catch(
        handleCatchError(this.platform),
      );
    } catch (e) {
      console.error(e);
      this.platform.showError(getErrorMessage(e));
      status = false;
    }
    if (!status) {
      // trigger aborted
      cancelBatch?.();
      this.setBusy(false);
      if (this.maskPageOnPost) maskPageSignal.postCount.set((v) => v - 1);
      return false;
    }

    // check for errors again after trigger
    if (this.hasErrors()) {
      this.platform.showError(
        `You must resolve all the validation errors before submiting it.

${this.getAllErrors()}
`,
      );
      cancelBatch?.();
      this.setBusy(false);
      if (this.maskPageOnPost) maskPageSignal.postCount.set((v) => v - 1);
      return false;
    }
    await dirtyRecords
      .reduce<Promise<unknown>>(
        (promise, record) =>
          promise.then(() => {
            if (record._rs === 'I') {
              invokeTrigger(this.beforeInsertStoreRecordTrigger, { record });
            }
            if (record._rs === 'D') {
              invokeTrigger(this.beforeDeleteRecordTrigger, { record });
            }
            return Promise.resolve(true);
          }),
        Promise.resolve(true),
      )
      .catch(handleCatchError);
  };

  handleTriggerAfterDataStoreSave = async (
    dbRows: DBRow<T>[],
    statusData: {
      insertIds: string[];
      updateIds: string[];
      deleteIds: string[];
    },
  ) => {
    await invokeTrigger(this.afterSaveTrigger);
    await statusData.insertIds.reduce<Promise<unknown>>(
      (promise, id) =>
        promise.then(() => {
          const record = dbRows.find((r) => {
            const _id = r._cid ?? r._id;
            return _id === id;
          });
          if (record) {
            invokeTrigger(this.afterInsertStoreRecordTrigger, { record });
          }
          return Promise.resolve(true);
        }),
      Promise.resolve(true),
    );
    await statusData.deleteIds.reduce<Promise<unknown>>(
      (promise, id) =>
        promise.then(() => {
          const record = dbRows.find((r) => {
            const _id = r._id ?? r._cid;
            return _id === id;
          });
          if (record) {
            invokeTrigger(this.afterDeleteRecordTrigger, { record });
          }
          return Promise.resolve(true);
        }),
      Promise.resolve(true),
    );
  };

  processUpdateRecord = (
    id: string,
    partialRecord: Partial<DBRow<T>>,
    skipDirty = false,
  ): Row<T> => {
    let _currentRecord = this.getRecord(id);
    if (isFromDB(_currentRecord)) {
      let originalRecord = this.getOriginalRecord(id);
      if (!originalRecord) {
        // no originalRecord means... it's the first time this record is being updated...
        originalRecord = _currentRecord;
        this.setOriginalRecord(id, originalRecord);
      }
      const currentRecord = { ..._currentRecord } as DBRow<T>;
      let _orig = currentRecord._orig
        ? { ...currentRecord._orig }
        : ({} as Partial<T>);
      const _newKeys: string[] = currentRecord._newKeys
        ? [...currentRecord._newKeys]
        : [];
      (Object.keys(partialRecord) as (keyof T)[]).forEach((key) => {
        const newVal = partialRecord[key];
        const oldVal = currentRecord[key];
        if (!areEqualShallow(oldVal, newVal)) {
          if (!skipDirty) {
            if (!Object.prototype.hasOwnProperty.call(_orig, key)) {
              _orig = {
                ..._orig,
                [key]: isNotEmpty(oldVal) ? oldVal : NULL_SYMBOL,
              };
              if (oldVal === undefined) {
                _newKeys.push(key as string);
              }
            } else if (
              originalRecord &&
              (areEqualShallow(originalRecord[key], newVal) ||
                (originalRecord[key] === undefined && isEmpty(newVal)))
            ) {
              delete _orig[key];
            }
          }
          // @ts-ignore TODO
          currentRecord[key] = newVal;
        }
      });
      if (Object.keys(_orig).length === 0) {
        delete currentRecord._orig;
        currentRecord._rs = originalRecord._rs;
        this.removeOriginalRecord(id);
      } else {
        currentRecord._orig = _orig;
        currentRecord._rs = 'U';
      }
      if (_newKeys.length) {
        currentRecord._newKeys = _newKeys;
      }
      _currentRecord = currentRecord;
    } else {
      _currentRecord = { ..._currentRecord, ...partialRecord, _rs: 'I' };
    }
    return _currentRecord;
  };

  updateRecord = (
    id: string,
    partialRecord: Partial<DBRow<T>>,
    skipDirty = false,
  ) => {
    this.setRecord(id, this.processUpdateRecord(id, partialRecord, skipDirty));
  };

  deleteTreeRecord = async (id: string, treeSateRecord: TreeNodeState) => {
    const children = treeSateRecord.children;
    for (let i = 0; i < children.length; i++) {
      const node: TreeNodeState = this.platform.getter([
        ...this.treeStatePath,
        'state',
        children[i],
      ]);
      if (node.children.length > 0) {
        this.deleteTreeRecord(children[i], node);
      } else {
        this.deleteRecord(children[i]);
      }
    }
    this.deleteRecord(id);
  };

  deleteRecord = async (id: string) => {
    const targetRecord = this.getRecord(id);
    if (isFromDB(targetRecord) && this.itemType !== 'LocalDataStore') {
      let originalRecord = this.getOriginalRecord(id);
      if (!originalRecord) {
        // no originalRecord means... it's the first time this record is being updated...
        originalRecord = { ...targetRecord };
        this.setOriginalRecord(id, originalRecord);
        const index = this.recordsOrder().indexOf(id);
        this.platform.setter([...this.deletedRecordsIndexMapPath, id], index);
      }
      this.setRecordStatus(id, 'D');
    } else {
      this.deleteFromStore(id);
    }
  };

  isAttributeDirty = (
    recordOrId: string | Row<T>,
    attribute: keyof T,
  ): boolean => {
    const record =
      typeof recordOrId === 'string' ? this.getRecord(recordOrId) : recordOrId;
    if (record?._orig?.[attribute] !== undefined) {
      return record[attribute] !== record._orig[attribute];
    }
    return false;
  };

  setCurrentRecordId = (
    id?: string | null,
    replaced?: boolean,
    setFirstRecord?: boolean,
  ) => {
    if (id === this.getCurrentRecordId()) {
      return;
    }
    if (id) {
      const index = this.recordsOrder().indexOf(id);
      const recordsLength = Object.keys(this.records()).length;
      if (index === -1) {
        if (!setFirstRecord) {
          this.platform.showError(
            `Developer Error: Record [${id}] being set as current record doesn't exists in data store [${this.ds}] with alias [${this.alias}]!`,
          );
          return;
        }
        if (recordsLength === 0 && setFirstRecord) {
          const recordIdFirst = this.recordsOrder()[0];
          if (recordIdFirst) {
            this.platform.setter(this.currentRecordIdPath, recordIdFirst);
            if (!replaced) {
              this.pubsub.pub('OnRecordActive', { id: recordIdFirst });
            }
          }
          return;
        }
      }
    }
    this.platform.setter(this.currentRecordIdPath, id);
    if (id) {
      const record = this.getRecord(id);
      const rowIndex = this.recordsOrder().indexOf(id);
      invokeTrigger(this.onRecordActiveTrigger, {
        data: {
          record,
          recordId: id,
          rowIndex,
        },
      });
    }
    if (!replaced) {
      this.pubsub.pub('OnRecordActive', { id });
    }
  };

  getCurrentRecordId = (): string | undefined =>
    this.platform.getter(this.currentRecordIdPath) ?? undefined;

  isStoreDirty = (): boolean => {
    if (Object.keys(this.originalRecords()).length > 0) {
      return true;
    }
    return this.dirtyRecords().length > 0;
  };

  isRecordDirty = (id: string): boolean => {
    return !!this.getOriginalRecord(id);
  };

  resetRecord = (id: string) => {
    const originalRecord = this.getOriginalRecord(id);
    if (originalRecord) {
      this.setRecord(id, originalRecord);
      this.removeOriginalRecord(id);
      this.platform.remover([...this.deletedRecordsIndexMapPath, id]);
    }
  };

  reset = () => {
    if (this.isStoreDirty() || this.hasNewRecords()) {
      ReactDOM.unstable_batchedUpdates(() => {
        const id = this.getCurrentRecordId();
        const records = { ...this.records() };
        const _originalRecords = this.originalRecords();
        Object.keys(_originalRecords).forEach((_id) => {
          records[_id] = _originalRecords[_id];
        });
        Object.keys(records).forEach((_id) => {
          if (records[_id]._rs !== 'Q') {
            delete records[_id];
          }
        });
        this.setRecords(records);
        const currentOrder = this.recordsOrder().filter(
          (_id) => !!records[_id],
        );
        const extraRecordIds = Object.keys(records).filter(
          (_id) => currentOrder.indexOf(_id) === -1,
        );
        const recordsOrder = [...currentOrder, ...extraRecordIds];
        if (recordsOrder.length) {
          this.setRecordsOrder(recordsOrder);
          if (!(id && records[id])) {
            this.setCurrentRecordId(recordsOrder[0]);
          }
        } else {
          this.setRecordsOrder([]);
          this.setCurrentRecordId(null);
        }
        this.setOriginalRecords({});
      });
      this.clearAllErrors();
      this.platform.forceUpdater(emptyFunction);
    }
  };

  getCurrentRecord = () => {
    const recordId = this.getCurrentRecordId();
    return recordId ? this.getRecord(recordId) : null;
  };

  resetCurrentRecord = () => {
    const recordId = this.getCurrentRecordId();
    if (recordId) this.resetRecord(recordId);
  };

  removeFromTreeState = (id: string) => {
    const node: TreeNodeState = this.platform.getter([
      ...this.treeStatePath,
      'state',
      id,
    ]);
    const parentId = node?.parent;
    if (parentId) {
      const parentNode: TreeNodeState = this.platform.getter([
        ...this.treeStatePath,
        'state',
        parentId,
      ]);
      if (parentNode) {
        const recordTreeState = {
          expanded: node?.expanded ? node?.expanded : true,
          children: parentNode?.children?.filter((r) => r !== id),
          loaded: true,
          parent: parentNode?.parent,
        };
        this.platform.setter(
          [...this.treeStatePath, 'state', parentId],
          recordTreeState,
        );
      }
    }
    this.platform.remover([...this.treeStatePath, 'state', id]);
  };

  deleteFromStore = (id: string) => {
    if (this.getTreeState()?.initialized) {
      // Need to remove the value from the tree state after deleting the record from store.
      this.removeFromTreeState(id);
    }
    const index = this.recordsOrder().indexOf(id);
    const oriIndex = this.originalRecordsOrder().indexOf(id);
    this.removeOriginalRecordOrderId(oriIndex);
    this.removeRecordOrderId(index);
    this._removeRecord(id);
    this.removeOriginalRecord(id);
    this.deSelectRecord(id);
    if (index !== -1) {
      const order = this.recordsOrder();
      const idVal = order[index] ?? order[order.length];
      if (!isEmpty(idVal)) {
        this.setCurrentRecordId(idVal);
      } else {
        this.setCurrentRecordId(null);
      }
    }
  };

  getSize = () => {
    return this.recordsOrder().length;
  };

  _getPrimaryKeySync = (
    pkAttributes: IoDatasourceAttributes[],
    r: DBRow<T>,
  ): string => {
    if (this.ignorePKDuplicate) return `r${this._idCounter++}`;
    const pk = pkAttributes.reduce(
      (key, attr) =>
        `${key}${key === '' ? '' : '-'}${
          r[attr.attributeCode as unknown as keyof T] ?? `r${this._idCounter++}`
        }`,
      '',
    );
    if (!pk) {
      throw Error(`Empty primary keys value for record ${JSON.stringify(r)}!`);
    }
    return pk;
  };

  validateAndGetPrimaryKeyAttributes = async (): Promise<
    IoDatasourceAttributes[]
  > => {
    const pkAttributes = (await this.getPrimaryKeyAttributes()) ?? [];
    if (!pkAttributes.length) {
      this.platform.showError(
        `Missing primary keys for dataSource '${this.ds}'. You must define atleast one primary key for this dataSource!`,
      );
      throw Error('Missing primary keys!');
    }
    return pkAttributes;
  };

  getPrimaryKey = async (r: DBRow<T>): Promise<string> => {
    if (this.ignorePKDuplicate) return `r${this._idCounter++}`;
    const pkAttributes = await this.validateAndGetPrimaryKeyAttributes();
    return this._getPrimaryKeySync(pkAttributes, r);
  };

  private async populateId(rows: DBRow<T>[]) {
    const pkAttributes = await this.validateAndGetPrimaryKeyAttributes();
    rows.forEach(async (r) => {
      if (!r._id) {
        r._id = this._getPrimaryKeySync(pkAttributes, r);
      }
    });
  }

  createNewBulk = async (rows: T[], insertOnTop = false): Promise<void> => {
    const newOrder: string[] = [];
    const newRows = rows.reduce((m, r) => {
      const _cid = this.ignorePKDuplicate
        ? `r${this._idCounter++}`
        : newRecordID();
      newOrder.push(_cid);
      const row = { ...r, _rs: 'I', _cid } as NewRow<T>;
      m[_cid] = row;
      return m;
    }, {} as Records<T>);
    this.setRecords({ ...this.records(), ...newRows });
    const order = this.recordsOrder();

    this.setRecordsOrder(
      insertOnTop ? [...newOrder, ...order] : [...order, ...newOrder],
    );

    this.setCurrentRecordId(newOrder[0]);
  };

  async processServerResponse(rows: DBRow<T>[]): Promise<Records<T>> {
    const recordsOrder: string[] = [];
    const records: Records<T> = {};
    const pkAttributes = await this.validateAndGetPrimaryKeyAttributes();
    for (let i = 0; i < rows.length; i++) {
      const cr = { ...rows[i] };
      cr._id = this._getPrimaryKeySync(pkAttributes, cr);
      if (records[cr._id] || this.recordsOrder().includes(cr._id)) {
        this.platform.showError(
          `Duplicate primary key value '${cr._id}' found in dataSource '${this.ds}' with alias '${this.alias}'! Make sure to choose the right combination of primary keys that are unique!.`,
        );
      }
      records[cr._id] = cr;
      recordsOrder.push(cr._id);
    }

    if (this.reverse) {
      recordsOrder.reverse();
    }
    ReactDOM.unstable_batchedUpdates(() => {
      this.platform.batchUpdater(() => {
        this.setRecords({ ...this.records(), ...records });
        // this.setOriginalRecords({ ...this.originalRecords(), ...records });
        if (this.reverse) {
          this.setRecordsOrder([...recordsOrder, ...this.recordsOrder()]);
          this.setOriginalRecordsOrder([
            ...recordsOrder,
            ...this.originalRecordsOrder(),
          ]);
        } else {
          this.setRecordsOrder([...this.recordsOrder(), ...recordsOrder]);
          this.setOriginalRecordsOrder([
            ...this.originalRecordsOrder(),
            ...recordsOrder,
          ]);
        }
        this.hasMore = recordsOrder.length >= this.maxRows;
        let currentRecordId = this.getCurrentRecordId();
        if (
          currentRecordId &&
          this.recordsOrder().indexOf(currentRecordId) === -1
        ) {
          currentRecordId = '';
        }
        currentRecordId = currentRecordId || this.recordsOrder()[0];
        this.setCurrentRecordId(currentRecordId);
      });
    });
    this.previousResult = records;
    await invokeTrigger(this.afterQueryTrigger);
    // force update the state
    this.platform.forceUpdater(emptyFunction);
    this.storeQueried = true;
    if (this.getTreeState()?.initialized) {
      this.loadTreeState();
    }

    return records;
  }

  getByNodeId = (nodeId: string, recordId: string) => {
    const nodeIdAttribute = this.getTreeState().nodeIdAttribute;
    return this.recordsOrder().find(
      (rid: string) =>
        (this.records()[rid][nodeIdAttribute] as unknown as string) ===
          nodeId && recordId !== rid,
    );
  };

  getChildrenForParent = (
    recordsOrder: Array<string>,
    records: Records<T>,
    parentNodeId: string,
    recordId: string,
  ) => {
    const parentNodeIdAttribute = this.getTreeState().parentNodeIdAttribute;

    return recordsOrder.filter(
      (rid) =>
        parentNodeId &&
        records[rid] &&
        (records[rid][parentNodeIdAttribute] as unknown as string) ===
          parentNodeId &&
        recordId !== rid,
    );
  };

  addToTreeState = (
    parentRecordId: string | null,
    recordId: string,
    initialExpanded: boolean,
    records: Records<T>,
    order: Array<string>,
  ) => {
    const nodeIdAttribute = this.getTreeState().nodeIdAttribute;
    const children = this.getChildrenForParent(
      order,
      records,
      records[recordId][nodeIdAttribute] as unknown as string,
      recordId,
    );
    const node: TreeNodeState = this.platform.getter([
      ...this.treeStatePath,
      'state',
      recordId,
    ]);
    const recordTreeState = {
      expanded: node?.expanded ? node?.expanded : initialExpanded,
      children,
      loaded: true,
      parent: parentRecordId,
    };
    this.platform.setter(
      [...this.treeStatePath, 'state', recordId],
      recordTreeState,
    );
    for (let i = 0; i < children.length; i++) {
      this.addToTreeState(
        recordId,
        children[i],
        initialExpanded,
        records,
        order,
      );
    }
  };

  addToTreeRecordsOrder = (
    recordId: string,
    newRecordsOrder: string[],

    ids?: string[],
  ) => {
    newRecordsOrder.push(recordId);
    const treeSateRecord: TreeNodeState = this.platform.getter([
      ...this.treeStatePath,
      'state',
      recordId,
    ]);
    const children = treeSateRecord?.expanded ? treeSateRecord.children : [];

    for (let i = 0; i < children.length; i++) {
      if (
        !ids?.length ||
        ids.includes(children[i]) ||
        treeSateRecord.expanded
      ) {
        this.addToTreeRecordsOrder(children[i], newRecordsOrder, ids);
      }
    }
  };

  makeTreeState = (initialExpaned: boolean) => {
    const parentNodeIdAttribute = this.getTreeState().parentNodeIdAttribute;
    const rootRecords = this.recordsOrder().filter((rid) => {
      const parentNodeId = this.records()[rid][
        parentNodeIdAttribute
      ] as unknown as string;
      const parentNodeRecordId = this.getByNodeId(parentNodeId, rid);
      return !(
        parentNodeId &&
        parentNodeRecordId &&
        this.recordsOrder().includes(parentNodeRecordId)
      );
    });
    for (let i = 0; i < rootRecords.length; i++) {
      this.addToTreeState(
        null,
        rootRecords[i],
        initialExpaned,
        this.records(),
        this.originalRecordsOrder(), //this.recordsOrder(),
      );
    }
  };

  updateTreeStateRecords(records: DBRow<T>[]) {
    if (records) {
      const parentNodeIdAttribute = this.getTreeState().parentNodeIdAttribute;
      const nodeIdAttribute = this.getTreeState().nodeIdAttribute;

      for (let i = 0; i < records.length; i++) {
        const rCid = records[i]._cid;
        const record = records[i][nodeIdAttribute] as unknown as string;
        const parentRecord = records[i][
          parentNodeIdAttribute
        ] as unknown as string;

        if (this.treeStatePath && rCid) {
          const node: TreeNodeState = this.platform.getter([
            ...this.treeStatePath,
            'state',
            rCid,
          ]);
          const recordTreeState = {
            expanded: node?.expanded ? node?.expanded : false,
            children: node.children,
            loaded: true,
            parent: parentRecord,
          };
          this.platform.setter(
            [...this.treeStatePath, 'state', record],
            recordTreeState,
          );
        }
      }
    }
  }

  makeNewTreeStateRecordsOrder = (ids?: string[]) => {
    const recordsOrder = ids?.length ? ids : this.recordsOrder();
    const newRecordsOrder: string[] = [];
    for (let i = 0; i < recordsOrder.length; i++) {
      this.addToTreeRecordsOrder(recordsOrder[i], newRecordsOrder, ids);
    }
    return newRecordsOrder;
  };

  makeTreeStateRecordsOrder = (ids?: string[]) => {
    const recordsOrder = ids?.length ? ids : this.recordsOrder();
    const parentNodeIdAttribute = this.getTreeState().parentNodeIdAttribute;
    const rootRecords = recordsOrder.filter((rid) => {
      const parentNodeId = this.records()[rid][
        parentNodeIdAttribute
      ] as unknown as string;
      const parentNodeRecordId = this.getByNodeId(parentNodeId, rid);
      return !(
        parentNodeId &&
        parentNodeRecordId &&
        recordsOrder.includes(parentNodeRecordId)
      );
    });
    const newRecordsOrder: string[] = [];
    for (let i = 0; i < rootRecords.length; i++) {
      this.addToTreeRecordsOrder(rootRecords[i], newRecordsOrder, ids);
    }
    return newRecordsOrder;
  };

  initializeTreeState = async (
    nodeIdAttribute: keyof T,
    parentNodeIdAttribute: keyof T,
    initialExpanded: boolean,
    force: boolean,
  ): Promise<boolean> => {
    if (!(nodeIdAttribute && parentNodeIdAttribute)) {
      return false;
    }
    // should be called after the store has records with only top nodes queried
    let storeTreeMetaData = this.platform.getter<StoreTreeMetaData<T>>(
      this.treeStatePath,
    );
    const recordsLength = Object.keys(this.records()).length;
    const treeStateRecordsLength = Object.keys(
      storeTreeMetaData?.state ?? {},
    ).length;
    if (
      recordsLength === treeStateRecordsLength &&
      storeTreeMetaData?.initialized
    ) {
      return true;
    }
    // console.log('data before tree initialized', this.recordsOrder());
    storeTreeMetaData = {
      state: {},
      nodeIdAttribute,
      parentNodeIdAttribute,
      initialized: true,
      initialExpanded,
    };
    this.platform.setter(this.treeStatePath, storeTreeMetaData);
    if (this.storeQueried) {
      this.loadTreeState();
    }
    return true;
  };

  loadTreeState = async (): Promise<boolean> => {
    const initialExpanded = this.getTreeState().initialExpanded;
    this.makeTreeState(initialExpanded);
    // console.log(this.platform.getter(this.treeStatePath));
    const newRecordsOrder = this.makeTreeStateRecordsOrder([]);
    // console.log('Records after initializing', newRecordsOrder);
    // this.platform.setter([...this.treeStatePath, 'initialized'], true);
    this.setRecordsOrder(newRecordsOrder);

    return true;
  };

  getTreeState = (): StoreTreeMetaData<T> => {
    return this.platform.getter(this.treeStatePath);
  };

  getTreeNodeState = (recordId: string): TreeNodeState => {
    return this.platform.getter([...this.treeStatePath, 'state', recordId]);
  };

  queryChildren = async (nid: string): Promise<string[]> => {
    const parentNodeIdAttribute = this.getTreeState().parentNodeIdAttribute;
    const currentRecodsOrder = this.recordsOrder();
    const curLen = currentRecodsOrder.length;
    await this._query({
      props: { filter: [{ [parentNodeIdAttribute]: { is: nid } }] },
      noClear: true,
    });
    const updatedRecordsOrder = this.recordsOrder();
    const children = updatedRecordsOrder.slice(curLen);
    this.setRecordsOrder(currentRecodsOrder);
    return children;
  };

  expandTreeNode = async (id: string, nid: string): Promise<void> => {
    // query for children if not already queried
    const ids: string[] = [];
    const node: TreeNodeState = this.platform.getter([
      ...this.treeStatePath,
      'state',
      id,
    ]);
    if (node?.expanded) {
      return;
    }
    const children: string[] = node.children;

    // if (!children || !children.length) {
    //   children = await this.queryChildren(nid);
    // }
    // add children to records order after parent
    const recordsOrder = this.recordsOrder();
    const index = recordsOrder.indexOf(id);
    const recordsToAdd: string[] = [];
    for (let i = 0; i < children.length; i++) {
      this.addToTreeRecordsOrder(children[i], recordsToAdd, ids);
    }
    this.setRecordsOrder([
      ...recordsOrder.slice(0, index + 1),
      ...recordsToAdd,
      ...recordsOrder.slice(index + 1),
    ]);

    this.platform.setter(
      [...this.treeStatePath, 'state', id, 'expanded'],
      true,
    );
  };

  removeRecordFromTreeRecordsOrder = (
    recordId: string,
    newRecordsOrder: string[],
  ) => {
    const node: TreeNodeState = this.platform.getter([
      ...this.treeStatePath,
      'state',
      recordId,
    ]);
    const children = node?.expanded ? node.children : [];
    for (let i = 0; i < children.length; i++) {
      this.removeRecordFromTreeRecordsOrder(children[i], newRecordsOrder);
    }
    const index = newRecordsOrder.indexOf(recordId);
    newRecordsOrder[index] = '_removed';
  };

  getNodeDepth = (id: string): number => {
    const node: TreeNodeState = this.platform.getter([
      ...this.treeStatePath,
      'state',
      id,
    ]);
    if (!node?.parent) {
      return 0;
    }
    return 1 + this.getNodeDepth(node.parent);
  };

  collapseTree = (id: string): void => {
    // remove children from treeState, recordsOrder
    const node: TreeNodeState = this.platform.getter([
      ...this.treeStatePath,
      'state',
      id,
    ]);
    if (!node.expanded) {
      return;
    }
    const newRecordsOrder = [...this.recordsOrder()];
    for (let i = 0; i < node.children.length; i++) {
      this.removeRecordFromTreeRecordsOrder(node.children[i], newRecordsOrder);
    }
    this.setRecordsOrder(newRecordsOrder.filter((rid) => rid !== '_removed'));
    // set expanded false for id
    this.platform.setter(
      [...this.treeStatePath, 'state', id, 'expanded'],
      false,
    );
  };

  expandAll = () => {
    // console.log('expanding');
    const treeState = this.getTreeState().state;
    const keys = Object.keys(treeState);
    keys.forEach((key) => {
      this.platform.setter(
        [...this.treeStatePath, 'state', key, 'expanded'],
        true,
      );
    });
    const newRecordsOrder = this.makeTreeStateRecordsOrder([]);
    this.setRecordsOrder(newRecordsOrder);
  };

  collapseAll = () => {
    // console.log('collapsing');
    const treeState = this.getTreeState().state;
    const keys = Object.keys(treeState);
    keys.forEach((key) => {
      this.platform.setter(
        [...this.treeStatePath, 'state', key, 'expanded'],
        false,
      );
    });
    const newRecordsOrder = this.makeTreeStateRecordsOrder([]);
    this.setRecordsOrder(newRecordsOrder);
  };

  sub = <K extends keyof StoreEventMap<T>>(
    event: K,
    listener: (_event: K, props: StoreEventMap<T>[K]) => Promise<void>,
  ): (() => void) => {
    if (event === 'OnRecordActive' && this.getCurrentRecordId()) {
      setTimeout(() => {
        // @ts-ignore OnRecordActive
        listener('OnRecordActive', { id: this.getCurrentRecordId() });
      }, 0);
    }
    return this.pubsub.sub(event, listener);
  };

  duplicateRecord = async (
    record: Row<T>,
    markAsDirty?: boolean,
    addOnTop?: boolean,
    setData?: Record<string, unknown>,
    addAfter?: boolean,
    addBefore?: boolean,
    addBottom?: boolean,
  ) => {
    if (!record) return;
    let dupRecord: NewRow<T> = {};
    const primaryKeyAttributes = await this.getPrimaryKeyAttributes();
    const primaryAttributesCodes = primaryKeyAttributes.map(
      (attribute) => attribute.attributeCode,
    );
    Object.keys(record).forEach((key) => {
      if (
        !(
          EXCLUDE_ATTRIBUTES.includes(key) ||
          primaryAttributesCodes.includes(key)
        )
      ) {
        // @ts-ignore dynamic value
        dupRecord[key] = record[key];
      }
    });
    dupRecord = { ...dupRecord, ...setData };
    this.createNew({
      partialRecord: dupRecord,
      rs: markAsDirty ? 'I' : 'N',
      addOnTop: addOnTop ?? false,
      addAfter: addAfter ? this.getCurrentRecordId() : '',
      addBefore: addBefore ? this.getCurrentRecordId() : '',
      addBottom: addBottom ?? false,
    });
  };

  copyRecord = async (
    targetStore: Store<unknown>,
    createAsNew?: boolean,
    recordId?: string | undefined | null,
  ): Promise<string | undefined> => {
    let id;
    let sourceRecord: Row<T> | null;
    if (!recordId) {
      sourceRecord = this.getCurrentRecord();
    } else {
      sourceRecord = this.getRecord(recordId);
    }
    const targetRecord = targetStore.getCurrentRecord();
    if (!sourceRecord) return;
    if (!targetStore) return;
    if (!(targetRecord || createAsNew)) return;
    const row: Record<string, unknown> = {};
    const primaryKeyAttributes = await targetStore.getPrimaryKeyAttributes();
    const primaryAttributesCodes = primaryKeyAttributes.map(
      (attribute) => attribute.attributeCode,
    );
    const targetAttributes = await targetStore.getAttributes();
    const targetAttributeCodes = targetAttributes.map(
      (attribute) => attribute.attributeCode,
    );
    Object.keys(sourceRecord).forEach(async (key) => {
      if (targetAttributeCodes.includes(key)) {
        if (
          !(
            EXCLUDE_ATTRIBUTES.includes(key) ||
            primaryAttributesCodes.includes(key)
          )
        ) {
          // @ts-ignore dynamic value
          row[key] = sourceRecord[key];
        }
      }
    });
    if (createAsNew) {
      id = await targetStore.createNew({ partialRecord: row, rs: 'I' });
    } else {
      const targetRecordId = targetStore.getCurrentRecordId();
      if (targetRecordId) targetStore.updateRecord(targetRecordId, row);
      id = targetRecordId;
    }
    return id;
  };

  deleteSelectedRecords = async () => {
    const records: string[] = this.selectedRecordIds();
    const all = [];
    for (let i = 0; i < records.length; i++) {
      all.push(this.deleteRecord(records[i]));
    }
    if (all.length) {
      await Promise.all(all);
    }
  };
}

export default Store;
